import * as React from "react";
import { parsePhoneNumber, isValidPhoneNumber, getCountryCallingCode, AsYouType, type CountryCode } from "libphonenumber-js";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import { COUNTRIES, getSortedCountries, type Country } from "@/constants/countries";
import { useTranslation } from "react-i18next";
import { parsePhoneNumberStructured, formatPhoneNumber, convertFromE164 } from "@mangelapp/shared/utils";

export interface PhoneInputProps extends Omit<React.ComponentProps<"input">, "value" | "onChange" | "type"> {
  value?: string;
  onChange?: (value: string) => void;
  onBlur?: () => void;
  countryCode?: CountryCode;
  defaultCountry?: CountryCode;
  required?: boolean;
  error?: string;
  allowInvalid?: boolean;
  className?: string;
  disabled?: boolean;
}

// Map ISO country codes to libphonenumber country codes
// Some countries have different codes (e.g., GB vs UK)
const COUNTRY_CODE_MAP: Record<string, CountryCode> = {
  'GB': 'GB',
  'US': 'US',
  'DE': 'DE',
  'TR': 'TR',
  'FR': 'FR',
  'IT': 'IT',
  'ES': 'ES',
  'NL': 'NL',
  'BE': 'BE',
  'AT': 'AT',
  'CH': 'CH',
  'PL': 'PL',
  'SE': 'SE',
  'NO': 'NO',
  'DK': 'DK',
  'FI': 'FI',
  'GR': 'GR',
  'PT': 'PT',
  'IE': 'IE',
  'CZ': 'CZ',
  'HU': 'HU',
  'RO': 'RO',
  'BG': 'BG',
  'HR': 'HR',
  'SK': 'SK',
  'SI': 'SI',
  'LT': 'LT',
  'LV': 'LV',
  'EE': 'EE',
  'LU': 'LU',
  'MT': 'MT',
  'CY': 'CY',
  'CA': 'CA',
  'AU': 'AU',
  'NZ': 'NZ',
  'JP': 'JP',
  'CN': 'CN',
  'IN': 'IN',
  'BR': 'BR',
  'MX': 'MX',
  'AR': 'AR',
  'ZA': 'ZA',
  'KR': 'KR',
  'SG': 'SG',
  'MY': 'MY',
  'TH': 'TH',
  'ID': 'ID',
  'PH': 'PH',
  'VN': 'VN',
  'AE': 'AE',
  'SA': 'SA',
  'IL': 'IL',
  'EG': 'EG',
  'NG': 'NG',
  'KE': 'KE',
  'RU': 'RU',
  'UA': 'UA',
  'BY': 'BY',
  'KZ': 'KZ',
  'UZ': 'UZ',
  'PK': 'PK',
  'BD': 'BD',
  'LK': 'LK',
  'NP': 'NP',
  'MM': 'MM',
  'KH': 'KH',
  'LA': 'LA',
  'MN': 'MN',
  'GE': 'GE',
  'AM': 'AM',
  'AZ': 'AZ',
  'TM': 'TM',
  'TJ': 'TJ',
  'KG': 'KG',
  'AF': 'AF',
  'IR': 'IR',
  'IQ': 'IQ',
  'SY': 'SY',
  'JO': 'JO',
  'LB': 'LB',
  'YE': 'YE',
  'OM': 'OM',
  'KW': 'KW',
  'QA': 'QA',
  'BH': 'BH',
  'DZ': 'DZ',
  'MA': 'MA',
  'TN': 'TN',
  'LY': 'LY',
  'SD': 'SD',
  'ET': 'ET',
  'SO': 'SO',
  'DJ': 'DJ',
  'ER': 'ER',
  'UG': 'UG',
  'TZ': 'TZ',
  'RW': 'RW',
  'BI': 'BI',
  'CD': 'CD',
  'CG': 'CG',
  'CM': 'CM',
  'CF': 'CF',
  'TD': 'TD',
  'NE': 'NE',
  'ML': 'ML',
  'BF': 'BF',
  'SN': 'SN',
  'GM': 'GM',
  'GW': 'GW',
  'GN': 'GN',
  'SL': 'SL',
  'LR': 'LR',
  'CI': 'CI',
  'GH': 'GH',
  'TG': 'TG',
  'BJ': 'BJ',
  'AO': 'AO',
  'ZM': 'ZM',
  'ZW': 'ZW',
  'BW': 'BW',
  'NA': 'NA',
  'LS': 'LS',
  'SZ': 'SZ',
  'MW': 'MW',
  'MZ': 'MZ',
  'MG': 'MG',
  'MU': 'MU',
  'SC': 'SC',
  'KM': 'KM',
  'CV': 'CV',
  'ST': 'ST',
  'GQ': 'GQ',
  'GA': 'GA',
  'CO': 'CO',
  'VE': 'VE',
  'EC': 'EC',
  'PE': 'PE',
  'BO': 'BO',
  'PY': 'PY',
  'UY': 'UY',
  'CL': 'CL',
  'CR': 'CR',
  'PA': 'PA',
  'NI': 'NI',
  'HN': 'HN',
  'SV': 'SV',
  'GT': 'GT',
  'BZ': 'BZ',
  'JM': 'JM',
  'HT': 'HT',
  'DO': 'DO',
  'CU': 'CU',
  'TT': 'TT',
  'BB': 'BB',
  'GD': 'GD',
  'LC': 'LC',
  'VC': 'VC',
  'AG': 'AG',
  'BS': 'BS',
  'DM': 'DM',
  'KN': 'KN',
  'SR': 'SR',
  'GY': 'GY',
  'GF': 'GF',
  'FK': 'FK',
  'GS': 'GS' as CountryCode,
  'PN': 'PN' as CountryCode,
  'SH': 'SH',
  'AC': 'AC',
  'TA': 'TA',
  'IO': 'IO',
  'CX': 'CX',
  'CC': 'CC',
  'NF': 'NF',
  'AQ': 'AQ' as CountryCode,
  'BV': 'BV' as CountryCode,
  'HM': 'HM' as CountryCode,
  'TF': 'TF' as CountryCode,
  'UM': 'UM' as CountryCode,
  'AS': 'AS',
  'GU': 'GU',
  'MP': 'MP',
  'PR': 'PR',
  'VI': 'VI',
  'VG': 'VG',
  'AI': 'AI',
  'MS': 'MS',
  'TC': 'TC',
  'KY': 'KY',
  'BM': 'BM',
  'GI': 'GI',
  'AD': 'AD',
  'MC': 'MC',
  'SM': 'SM',
  'VA': 'VA',
  'LI': 'LI',
  'FO': 'FO',
  'GL': 'GL',
  'SJ': 'SJ',
  'AX': 'AX',
  'IS': 'IS',
  'GG': 'GG',
  'JE': 'JE',
  'IM': 'IM',
  'MH': 'MH',
  'FM': 'FM',
  'PW': 'PW',
  'KI': 'KI',
  'NR': 'NR',
  'TV': 'TV',
  'TO': 'TO',
  'WS': 'WS',
  'FJ': 'FJ',
  'VU': 'VU',
  'NC': 'NC',
  'PF': 'PF',
  'CK': 'CK',
  'NU': 'NU',
  'TK': 'TK',
  'SB': 'SB',
  'PG': 'PG',
  'BN': 'BN',
  'TL': 'TL',
  'TW': 'TW',
  'HK': 'HK',
  'MO': 'MO',
};

// Get libphonenumber country code from ISO code
function getPhoneCountryCode(isoCode: string): CountryCode | undefined {
  return COUNTRY_CODE_MAP[isoCode] || (isoCode as CountryCode);
}

// Get country calling code for display
function getCallingCode(countryCode: CountryCode): string {
  try {
    return `+${getCountryCallingCode(countryCode)}`;
  } catch {
    return '';
  }
}

export const PhoneInput = React.forwardRef<HTMLInputElement, PhoneInputProps>(
  ({ 
    value = '', 
    onChange, 
    onBlur,
    countryCode: controlledCountryCode,
    defaultCountry = 'DE',
    required = false,
    error,
    allowInvalid = false,
    className,
    disabled,
    ...props 
  }, ref) => {
    const { i18n, t } = useTranslation();
    const [internalCountryCode, setInternalCountryCode] = React.useState<CountryCode>(defaultCountry);
    const [phoneNumber, setPhoneNumber] = React.useState<string>('');
    const [isValid, setIsValid] = React.useState<boolean>(true);

    const countryCode = controlledCountryCode || internalCountryCode;

    // Parse existing value on mount or when value changes
    React.useEffect(() => {
      if (value) {
        // First, try to parse structured format +(CC)NNNNNNNN
        const structuredParsed = parsePhoneNumberStructured(value);
        if (structuredParsed) {
          // Convert country calling code to ISO country code for libphonenumber
          try {
            // Create a temporary E.164 number to get ISO country code
            const tempE164 = `+${structuredParsed.countryCode}${structuredParsed.nationalNumber}`;
            const parsed = parsePhoneNumber(tempE164);
            if (parsed) {
              setInternalCountryCode(parsed.country || defaultCountry);
              setPhoneNumber(structuredParsed.nationalNumber);
              setIsValid(parsed.isValid());
            } else {
              // Fallback: use default country
              setInternalCountryCode(defaultCountry);
              setPhoneNumber(structuredParsed.nationalNumber);
              setIsValid(true);
            }
          } catch {
            // Fallback: use default country
            setInternalCountryCode(defaultCountry);
            setPhoneNumber(structuredParsed.nationalNumber);
            setIsValid(true);
          }
        } else {
          // Try to parse E.164 format (backward compatibility)
          try {
            const parsed = parsePhoneNumber(value);
            if (parsed) {
              setInternalCountryCode(parsed.country || defaultCountry);
              setPhoneNumber(parsed.nationalNumber);
              setIsValid(parsed.isValid());
            } else {
              // Try to extract country from value
              const asYouType = new AsYouType();
              asYouType.input(value);
              const country = asYouType.getCountry();
              if (country) {
                setInternalCountryCode(country);
              }
              setPhoneNumber(value.replace(/^\+?\d{1,4}\s?/, ''));
            }
          } catch {
            // If parsing fails, try to extract number part
            const cleaned = value.replace(/^\+?\d{1,4}\s?/, '');
            setPhoneNumber(cleaned);
          }
        }
      } else {
        setPhoneNumber('');
        setIsValid(true);
      }
    }, [value, defaultCountry]);

    // Format phone number as user types
    const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const input = e.target.value;
      const asYouType = new AsYouType(countryCode);
      const formatted = asYouType.input(input);
      
      // Extract just the national number part (without country code)
      const nationalNumber = asYouType.getNumber()?.nationalNumber || input.replace(/\D/g, '');
      setPhoneNumber(nationalNumber);

      // Check if valid
      try {
        const fullNumber = `+${getCountryCallingCode(countryCode)}${nationalNumber}`;
        const valid = isValidPhoneNumber(fullNumber);
        setIsValid(!nationalNumber || valid);
        
        // Call onChange with structured format +(CC)NNNNNNNN if valid
        if (onChange) {
          if ((valid && nationalNumber) || (allowInvalid && nationalNumber)) {
            try {
              const parsed = parsePhoneNumber(fullNumber);
              if (parsed) {
                // Format as +(CC)NNNNNNNN
                const countryCallingCode = parsed.countryCallingCode;
                const formatted = formatPhoneNumber(countryCallingCode, parsed.nationalNumber);
                onChange(formatted);
              } else {
                // Fallback: format manually
                const countryCallingCode = getCountryCallingCode(countryCode);
                onChange(formatPhoneNumber(countryCallingCode, nationalNumber));
              }
            } catch {
              // Fallback: format manually
              const countryCallingCode = getCountryCallingCode(countryCode);
              onChange(formatPhoneNumber(countryCallingCode, nationalNumber));
            }
          } else {
            onChange('');
          }
        }
      } catch {
        setIsValid(false);
        if (onChange) {
          if (allowInvalid && nationalNumber) {
            const countryCallingCode = getCountryCallingCode(countryCode);
            onChange(formatPhoneNumber(countryCallingCode, nationalNumber));
          } else {
            onChange('');
          }
        }
      }
    };

    // Handle country change
    const handleCountryChange = (isoCode: string) => {
      const phoneCountryCode = getPhoneCountryCode(isoCode);
      if (phoneCountryCode) {
        setInternalCountryCode(phoneCountryCode);
        // Re-validate current phone number with new country
        if (phoneNumber) {
          try {
            const fullNumber = `+${getCountryCallingCode(phoneCountryCode)}${phoneNumber}`;
            const valid = isValidPhoneNumber(fullNumber);
            setIsValid(valid);
            
            if (onChange) {
              if (valid || allowInvalid) {
                try {
                  const parsed = parsePhoneNumber(fullNumber);
                  if (parsed) {
                    // Format as +(CC)NNNNNNNN
                    const countryCallingCode = parsed.countryCallingCode;
                    const formatted = formatPhoneNumber(countryCallingCode, parsed.nationalNumber);
                    onChange(formatted);
                  } else {
                    // Fallback: format manually
                    const countryCallingCode = getCountryCallingCode(phoneCountryCode);
                    onChange(formatPhoneNumber(countryCallingCode, phoneNumber));
                  }
                } catch {
                  // Fallback: format manually
                  const countryCallingCode = getCountryCallingCode(phoneCountryCode);
                  onChange(formatPhoneNumber(countryCallingCode, phoneNumber));
                }
              } else {
                onChange('');
              }
            }
          } catch {
            setIsValid(false);
            if (onChange) {
              onChange('');
            }
          }
        }
      }
    };

    // Get sorted countries for dropdown
    const sortedCountries = React.useMemo(() => {
      return getSortedCountries(i18n.language as 'en' | 'de' | 'tr');
    }, [i18n.language]);

    // Find current country in list
    const currentCountry = React.useMemo(() => {
      return sortedCountries.find(c => getPhoneCountryCode(c.code) === countryCode) || sortedCountries[0];
    }, [sortedCountries, countryCode]);

    return (
      <div className={cn("flex gap-2", className)}>
        {/* Country Code Selector */}
        <Select
          value={currentCountry.code}
          onValueChange={handleCountryChange}
          disabled={disabled}
        >
          <SelectTrigger className="!w-[80px] textbox-select">
            <SelectValue>
              {getCallingCode(countryCode)}
            </SelectValue>
          </SelectTrigger>
          <SelectContent className="max-h-[300px]">
            {sortedCountries.map((country) => {
              const phoneCountryCode = getPhoneCountryCode(country.code);
              if (!phoneCountryCode) return null;
              const callingCode = getCallingCode(phoneCountryCode);
              return (
                <SelectItem key={country.code} value={country.code} className="text-xs">
                  {callingCode} {country[i18n.language as 'en' | 'de' | 'tr']}
                </SelectItem>
              );
            })}
          </SelectContent>
        </Select>

        {/* Phone Number Input */}
        <div className="flex-1">
          <Input
            ref={ref}
            type="tel"
            value={phoneNumber}
            onChange={handlePhoneChange}
            onBlur={onBlur}
            placeholder="123456789"
            required={required}
            disabled={disabled}
            className={cn(
              error || (!isValid && phoneNumber) ? "border-red-500 focus:border-red-500" : ""
            )}
            {...props}
          />
          {(error || (!isValid && phoneNumber)) && (
            <p className="text-xs text-red-500 mt-1">
              {error || t('common:validation.invalidPhone')}
            </p>
          )}
        </div>
      </div>
    );
  }
);

PhoneInput.displayName = "PhoneInput";
